import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { IStorageCompany } from 'src/app/@interfaces/company.interface';
import { PaymentsService } from './payments.service';
import { IQuotation } from 'src/app/@interfaces/quotation.interface';
import {
  PdfConfiguration,
  PdfContent,
} from 'src/app/@shared/interfaces/pdf.interface';
import { PdfGeneratorService } from '../../../pdf-generator/pdf-generator.service';
import { GenerateContentPdfService } from '../../../services/files/generate-content-pdf.service';

const COMPANY_LOGOS: Record<string, string> = {
  '1': '../../../../assets/img/v1rotateBlack.png',
  '2': '../../../../assets/img/Ibexports.png',
  '4': '../../../../assets/img/Jens.png',
  '5': '../../../../assets/icons/letrasVersa.png',
  '6': '../../../../assets/img/Parabricentro.png',
  '7': '../../../../assets/img/v1rotateBlack.png',
  '8': '../../../../assets/img/Parabricentro.png',
  '9': '../../../../assets/img/v1rotateBlack.png',
  '10': '../../../../assets/img/v1rotateBlack.png',
  '11': '../../../../assets/img/v1rotateBlack.png',
};

@Injectable({
  providedIn: 'root',
})
export class PaymentsPdfService {
  totalPaid: number = 0;
  companyObject = {} as IStorageCompany;
  companyName = '';
  constructor(
    private translate: TranslateService,
    private cookieAuthService: CookieAuthService,
    private paymentsService: PaymentsService,
    private pdfGeneratorService: PdfGeneratorService,
    private generateContentPdfService: GenerateContentPdfService,
  ) {}

  getAuthValues() {
    this.companyObject = this.cookieAuthService.getCompanyObject!;
    this.companyName = this.companyObject.name;
  }

  getCompanyLogo(): string {
    const companyId =
      this.cookieAuthService.getCompanyObject?.Id_company?.toString() ?? '';
    return COMPANY_LOGOS[companyId] ?? '';
  }

  async createPaymentInvoicePdf(
    open: boolean,
    state: string,
    quotation: IQuotation,
    image: any,
    paymentDescription: string,
    targetWindow: Window | null = null,
  ) {
    this.getAuthValues();
    const payments = await this.paymentsService.getInvoicePayments(
      quotation.id_factura!,
    );
    const validations = this.generateContentPdfService.pdfPaymentValidations(
      payments,
      quotation,
    );
    if (!validations) {
      this.pdfGeneratorService.closePendingWindow(targetWindow);
      return;
    }
    const paymentType =
      this.generateContentPdfService.getPdfPaymentType(payments);
    const paymentElements =
      await this.generateContentPdfService.generatePdfPaymentItems(payments);
    const watermark = this.generateContentPdfService.createWatermark(state);
    const content = await this.createInvoicePaymentContent(
      image,
      quotation,
      paymentType,
      paymentDescription,
      paymentElements,
    );
    const pdfConfiguration: PdfConfiguration = {
      openPDF: open,
      downloadPDF: false,
      watermark: watermark,
      content: content,
    };
    return this.pdfGeneratorService.createPdf(pdfConfiguration, targetWindow);
  }

  async createInvoicePaymentContent(
    image: any,
    quotation: IQuotation,
    paymentType: string,
    paymentDescription: string,
    paymentElements: any,
  ) {
    const layoutConfiguration =
      this.generateContentPdfService.createLayout('grey');
    const content: PdfContent[] = [
      {
        style: 'tableExample',
        table: {
          widths: ['*', '*', '*'],
          heights: [25, 25, 25],
          body: [
            [
              await this.generateContentPdfService.createPdfPaymentLogo(image),
              this.generateContentPdfService.createRowSpanObject(
                3,
                this.companyName,
              ),
              this.generateContentPdfService.createRowSpanObject(
                3,
                `Recibo\n ${quotation.contfac!.invoice.replace('I-', '')}`,
              ),
            ],
            ['', '', ''],
            ['', '', ''],
          ],
        },
      },
      {
        style: 'tableExample',
        color: '#444',
        table: {
          widths: ['auto', '*', 'auto', 'auto', 'auto', 'auto'],
          headerRows: 2,
          body: [
            [
              {
                style: 'header',
                border: [true, true, false, false],
                text: 'Recibimos de',
              },
              {
                style: 'textStyle',
                border: [false, true, true, true],
                colSpan: 3,
                text: `${quotation.cliente.nombre}`,
              },
              {
                text: '',
              },
              {
                text: '',
              },
              { style: 'header', text: 'Fecha Recibo' },
              { style: 'header', text: 'Forma Pago' },
            ],
            [
              {
                style: 'header',
                border: [true, false, false, false],
                text: 'NIT',
              },
              {
                style: 'textStyle',
                border: [false, true, true, true],
                text: `${quotation.cliente.doc_nit}`,
              },
              {
                style: 'header',
                border: [true, true, false, false],
                text: 'Telefono',
              },
              {
                style: 'textStyle',
                border: [false, true, true, true],
                text: `${quotation.cliente.telefono}`,
              },
              { style: 'textStyle', text: `${quotation.createdAt}` },
              {
                style: 'textStyle',
                text: `${this.translate.instant(
                  paymentType,
                )} ${paymentDescription}`,
              },
            ],
            [
              {
                style: 'header',
                border: [true, false, false, true],
                text: 'Direccion',
              },
              {
                style: 'textStyle',
                border: [false, true, true, true],
                text: `${quotation.cliente.direccion}`,
              },
              {
                style: 'header',
                border: [true, false, false, true],
                text: 'Ciudad',
              },
              {
                style: 'textStyle',
                border: [false, true, true, true],
                text: `${quotation.cliente.city}`,
              },
              { style: 'header', text: '-' },
              { style: 'textStyle', text: `` },
            ],
          ],
        },
        layout: layoutConfiguration,
      },
      {
        style: 'tableExample',
        table: {
          widths: ['*', 'auto'],
          body: [
            [
              { style: 'header', text: 'El valor de' },
              { style: 'textStyle', text: `${quotation.venta[0].total}` },
            ],
          ],
        },
        layout: layoutConfiguration,
      },
      {
        style: 'tableExample',
        table: {
          widths: [70, 70, 70, '*', 'auto'],
          body: paymentElements,
        },
        layout: layoutConfiguration,
      },
      {
        style: 'tableExample',
        table: this.generateContentPdfService.createPaymentObservations(),
        layout: layoutConfiguration,
      },
      {
        style: 'tableExample',
        table: this.generateContentPdfService.createPaymentSign(),
        layout: layoutConfiguration,
      },
    ];
    return content;
  }
}
